import styled from "@emotion/styled";
import { Person } from "@mui/icons-material";
import Color from "color";
import * as React from "react";

import getThumbnailUrl from "../utils/getThumbnailUrl";
import stringToColor from "../utils/stringToColor";

type AvatarProps = {
  user?: { uuid: string; name: string; pictureUrl: string | null };
  size?: number;
  round?: boolean;
} & React.ComponentProps<"div">;

interface ImageProps {
  size: number;
  round: boolean;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ user, size = 40, round = true, ...props }, ref) => {
    const [error, setError] = React.useState<boolean | undefined>(undefined);

    const didError = user && error;

    const handleError = React.useCallback(() => {
      if (user?.uuid) {
        setError(true);
      }
    }, [user?.uuid]);

    const handleLoad = React.useCallback(() => {
      if (user?.uuid) {
        setError(false);
      }
    }, [user?.uuid]);

    React.useEffect(() => {
      setError(undefined);
    }, [user?.uuid]);

    const bgColor = user ? stringToColor(user.name) : "#999";
    const fgColor = new Color(bgColor).isDark() ? "#fff" : "#000";

    if (user && user.pictureUrl && !didError) {
      const imgUrl = user.pictureUrl.includes("gravatar")
        ? user.pictureUrl
        : getThumbnailUrl(user.pictureUrl);

      return (
        <div ref={ref}>
          {didError !== false ? (
            <UserInitials
              size={size}
              round={round}
              style={{
                backgroundColor: bgColor,
                color: fgColor,
              }}
            >
              {user
                ? user.name
                    .split(" ")
                    .slice(0, 2)
                    .map((x) => x.charAt(0))
                    .join("")
                : null}
            </UserInitials>
          ) : null}
          <ProfileImg
            src={imgUrl}
            size={size}
            round={round}
            onError={handleError}
            onLoad={handleLoad}
            style={{ display: didError === undefined ? "none" : "block" }}
          />
        </div>
      );
    }

    if (!user) {
      return (
        <UserInitials
          size={size}
          round={round}
          style={{
            backgroundColor: bgColor,
            color: fgColor,
          }}
          ref={ref}
          {...props}
        >
          <Person sx={{ color: "white", width: size - 4, height: size - 4 }} />
        </UserInitials>
      );
    }

    return (
      <UserInitials
        size={size}
        round={round}
        style={{
          backgroundColor: bgColor,
          color: fgColor,
        }}
        ref={ref}
        {...props}
      >
        {user
          ? `${user.name
              .split(" ")
              .slice(0, 2)
              .map((x) => x.charAt(0))
              .join("")}`
          : null}
      </UserInitials>
    );
  },
);

Avatar.displayName = "Avatar";

const ProfileImg = styled.img`
  width: ${({ size }: ImageProps) => size}px;
  height: ${({ size }: ImageProps) => size}px;
  object-fit: cover;
  border-radius: ${({ round }: ImageProps) => (round ? "50%" : 0)};
  display: block;
  flex-shrink: 0;
`;

const UserInitials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ size }: ImageProps) => size / 1.8}px;
  font-weight: bold;
  width: ${({ size }: ImageProps) => size}px;
  height: ${({ size }: ImageProps) => size}px;
  border-radius: ${({ round }: ImageProps) => (round ? "50%" : 0)};
  flex-shrink: 0;
`;

export default Avatar;
